import { useEffect, useMemo, useRef, useState } from "react";
import { format, parseISO, subDays } from "date-fns";
import {
  CheckCircle2, XCircle, FileText, Clock, Drill,
  ChevronLeft, Download, Upload, FileArchive, FileSpreadsheet,
  CalendarIcon,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn, safeFormatDate } from "@/lib/utils";
import { shifts as shiftsApi, type ShiftRow } from "@/lib/api";
import { useRBAC } from "@/hooks/useRBAC";
import {
  exportShiftRowsToCSV, exportShiftRowsToZip, downloadFile,
  importShiftLogsFromCSV, type ShiftDataLike,
} from "@/lib/shiftExports";

type StatusFilter = "all" | "submitted" | "approved" | "rejected" | "synced";

/**
 * Shift Logs — org-wide list of every shift, with bulk import + multiple
 * export formats. Doubles as a power-user approvals surface (you can still
 * approve/reject submitted shifts from the detail view here).
 *
 * Mirrors Approvals.tsx structure on purpose for consistency.
 */
export default function ShiftLogs() {
  const { can } = useRBAC();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [rows, setRows] = useState<ShiftRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<ShiftRow | null>(null);
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState(false);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [startDate, setStartDate] = useState(() => format(subDays(new Date(), 30), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(() => format(new Date(), "yyyy-MM-dd"));

  // Import progress
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ done: number; total: number } | null>(null);

  async function load() {
    setLoading(true);
    try {
      const all = await shiftsApi.list({ limit: 1000 });
      // Newest first using submitted_at when available, else created_at.
      all.sort((a, b) => (b.submitted_at ?? b.created_at).localeCompare(a.submitted_at ?? a.created_at));
      setRows(all);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  // Filter rows for the visible table — exports use this same set.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      const d = (r.shift_data ?? {}) as ShiftDataLike;
      const sd = d.shiftDetails ?? {};

      if (statusFilter !== "all" && r.status !== statusFilter) return false;

      // Date filter against shiftDetails.date — fall back to created_at if missing.
      const shiftDate = sd.date || r.created_at.slice(0, 10);
      if (startDate && shiftDate < startDate) return false;
      if (endDate && shiftDate > endDate) return false;

      if (q) {
        const hay = `${sd.operatorName ?? ""} ${sd.rigId ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, statusFilter, startDate, endDate]);

  async function act(newStatus: "approved" | "rejected") {
    if (!selected) return;
    setActing(true);
    try {
      const data = (selected.shift_data ?? {}) as ShiftDataLike & { supervisorNotes?: string | null };
      const reviewedAt = new Date().toISOString();
      await shiftsApi.upsert({
        id: selected.id,
        app_slug: "operator",
        shift_data: { ...data, supervisorNotes: notes || data.supervisorNotes || null, status: newStatus, reviewedAt },
        status: newStatus,
        submitted_at: selected.submitted_at,
      });
      setSelected(null);
      setNotes("");
      await load();
    } finally {
      setActing(false);
    }
  }

  function handleExportCSV() {
    if (visible.length === 0) {
      toast.warning("No shifts in current view to export.");
      return;
    }
    const csv = exportShiftRowsToCSV(visible);
    if (!csv) return;
    const stamp = format(new Date(), "yyyyMMdd-HHmmss");
    downloadFile(csv, `strataworks-shifts-${stamp}.csv`, "text/csv;charset=utf-8;");
    toast.success(`Exported ${visible.length} shift(s) to CSV.`);
  }

  function handleExportXLS() {
    // Per Mick: download CSV labelled .xlsx is fine for now (no xlsx dep on dashboard).
    if (visible.length === 0) {
      toast.warning("No shifts in current view to export.");
      return;
    }
    const csv = exportShiftRowsToCSV(visible);
    if (!csv) return;
    const stamp = format(new Date(), "yyyyMMdd-HHmmss");
    downloadFile(csv, `strataworks-shifts-${stamp}.xlsx`, "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet");
    toast.success(`Exported ${visible.length} shift(s) to XLSX.`);
  }

  async function handleExportZIP() {
    if (visible.length === 0) {
      toast.warning("No shifts in current view to export.");
      return;
    }
    try {
      const blob = await exportShiftRowsToZip(visible);
      if (!blob) return;
      const stamp = format(new Date(), "yyyyMMdd-HHmmss");
      downloadFile(blob, `strataworks-shifts-${stamp}.zip`, "application/zip");
      toast.success(`Exported ${visible.length} shift(s) to ZIP.`);
    } catch (e) {
      console.error(e);
      toast.error("ZIP export failed — see console.");
    }
  }

  async function handleBulkUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset the input so picking the same file again triggers onChange.
    e.target.value = "";

    setImporting(true);
    setImportProgress(null);
    try {
      const text = await file.text();
      const { logs, errors } = importShiftLogsFromCSV(text);
      if (errors.length > 0) {
        errors.slice(0, 3).forEach((err) => toast.error(err));
      }
      if (logs.length === 0) {
        toast.error("No shifts found in CSV.");
        return;
      }

      // Batch upload via /api/shifts/bulk — one request per chunk instead of
      // one per row. Backend caps each batch at 1000; we use 500 to keep
      // request bodies under a couple of MB even with photo-heavy shifts.
      const CHUNK = 500;
      const submittedAt = new Date().toISOString();
      let successCount = 0;
      const failedChunks: number[] = [];
      setImportProgress({ done: 0, total: logs.length });

      for (let i = 0; i < logs.length; i += CHUNK) {
        const slice = logs.slice(i, i + CHUNK);
        try {
          const res = await shiftsApi.bulkUpsert(
            slice.map((item) => ({
              id: item.id,
              app_slug: "operator",
              shift_data: item.shift_data,
              status: "submitted",
              submitted_at: submittedAt,
            })),
          );
          successCount += res.upserted;
        } catch (err) {
          console.error("Bulk upload chunk failed", { from: i, count: slice.length }, err);
          failedChunks.push(i);
        }
        setImportProgress({ done: Math.min(i + CHUNK, logs.length), total: logs.length });
      }

      if (failedChunks.length > 0) {
        toast.error(`${failedChunks.length} batch(es) failed to upload — see console.`);
      }
      toast.success(`Uploaded ${successCount} of ${logs.length} shift(s).`);
      await load();
    } catch (err) {
      console.error(err);
      toast.error("Bulk upload failed — see console.");
    } finally {
      setImporting(false);
      setImportProgress(null);
    }
  }

  if (!can("manage:org")) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            You don't have permission to view shift logs.
          </CardContent>
        </Card>
      </div>
    );
  }

  // ------------- Detail view -------------
  if (selected) {
    const d = (selected.shift_data ?? {}) as ShiftDataLike & { supervisorNotes?: string | null; submittedAt?: string | null };
    const sd = d.shiftDetails ?? {};
    const holes = d.drillHoles ?? [];
    const delays = d.delays ?? [];
    const consumables = d.consumables ?? [];
    const totalMetres = holes.reduce((s, h) => s + (h.depth ?? 0), 0);
    const totalDelayMins = Math.round(delays.reduce((s, x) => s + (x.timeSeconds ?? 0), 0) / 60);
    const isReadOnly = selected.status !== "submitted";

    return (
      <div className="max-w-4xl mx-auto p-4 md:p-6 space-y-4">
        <Button variant="ghost" size="sm" onClick={() => { setSelected(null); setNotes(""); }}>
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to list
        </Button>

        <div className="flex items-center justify-between flex-wrap gap-2">
          <h1 className="text-2xl font-bold text-foreground">
            {sd.operatorName ?? "Unknown"} — {safeFormatDate(sd.date, "EEE d MMM yyyy", "no date")}
          </h1>
          <Badge variant="secondary" className="capitalize">{selected.status}</Badge>
        </div>

        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Shift details</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-sm">
            <div><span className="text-muted-foreground">Rig:</span> {sd.rigId ?? "—"} ({sd.shiftType ?? "—"} shift)</div>
            <div><span className="text-muted-foreground">Site / Location:</span> {sd.site ?? "—"} / {sd.location ?? "—"}</div>
            {d.submittedAt && (
              <div><span className="text-muted-foreground">Submitted:</span> {safeFormatDate(d.submittedAt, "d MMM yyyy, HH:mm")}</div>
            )}
            {d.notes && (
              <div><span className="text-muted-foreground">Operator notes:</span> {d.notes}</div>
            )}
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Drill className="h-4 w-4" /> Holes</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <div className="text-2xl font-bold">{holes.length}</div>
              <div className="text-muted-foreground">{totalMetres.toFixed(1)}m total</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Clock className="h-4 w-4" /> Delays</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <div className="text-2xl font-bold">{delays.length}</div>
              <div className="text-muted-foreground">{totalDelayMins} min total</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><FileText className="h-4 w-4" /> Consumables</CardTitle></CardHeader>
            <CardContent className="text-sm">
              <div className="text-2xl font-bold">{consumables.length}</div>
              <div className="text-muted-foreground">items logged</div>
            </CardContent>
          </Card>
        </div>

        {holes.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Drill holes</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2 px-4 font-medium">Hole</th>
                    <th className="py-2 px-4 font-medium">Depth</th>
                    <th className="py-2 px-4 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {holes.map((h, i) => (
                    <tr key={i} className="border-b border-border/50 last:border-0">
                      <td className="py-2 px-4 font-medium">{h.holeNumber ?? "—"}</td>
                      <td className="py-2 px-4">{(h.depth ?? 0).toFixed(1)}m</td>
                      <td className="py-2 px-4 capitalize">{h.status ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {delays.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Delays</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-foreground">
                    <th className="py-2 px-4 font-medium">Type</th>
                    <th className="py-2 px-4 font-medium">Duration</th>
                    <th className="py-2 px-4 font-medium">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {delays.map((x, i) => {
                    const mins = Math.round((x.timeSeconds ?? 0) / 60);
                    const h = Math.floor(mins / 60), m = mins % 60;
                    return (
                      <tr key={i} className="border-b border-border/50 last:border-0">
                        <td className="py-2 px-4 capitalize">{(x.delayType ?? "").replace(/-/g, " ")}</td>
                        <td className="py-2 px-4">{h > 0 ? `${h}h ${m}m` : `${m}m`}</td>
                        <td className="py-2 px-4 text-muted-foreground">{x.notes ?? ""}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

        {!isReadOnly && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Supervisor notes</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional — anything you want the operator to see."
                rows={3}
              />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => void act("rejected")} disabled={acting}>
                  <XCircle className="h-4 w-4 mr-1.5" /> Reject
                </Button>
                <Button onClick={() => void act("approved")} disabled={acting}>
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {isReadOnly && d.supervisorNotes && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Supervisor notes</CardTitle></CardHeader>
            <CardContent className="text-sm">{d.supervisorNotes}</CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ------------- List view -------------
  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-4">
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <FileText className="h-6 w-6" /> Shift Logs
        </h1>
        <p className="text-muted-foreground text-sm">All shifts across the organisation. Export, bulk-upload, or open one to review.</p>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" onClick={handleExportCSV} disabled={loading}>
          <Download className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
        <Button variant="outline" onClick={handleExportXLS} disabled={loading}>
          <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Export XLS
        </Button>
        <Button variant="outline" onClick={() => void handleExportZIP()} disabled={loading}>
          <FileArchive className="h-4 w-4 mr-1.5" /> Export ZIP
        </Button>
        <Button onClick={() => fileInputRef.current?.click()} disabled={importing}>
          <Upload className="h-4 w-4 mr-1.5" />
          {importing && importProgress
            ? `Uploaded ${importProgress.done} of ${importProgress.total}…`
            : importing
              ? "Uploading…"
              : "Bulk Upload CSV"}
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => void handleBulkUpload(e)}
        />
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Input
          placeholder="Search operator or rig…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="submitted">Submitted</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="synced">Synced</SelectItem>
          </SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn("w-[150px] justify-start text-left font-normal", !startDate && "text-muted-foreground")}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {startDate ? format(parseISO(startDate), "MMM dd, yyyy") : "Start"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 z-50" align="start">
            <Calendar
              mode="single"
              selected={startDate ? parseISO(startDate) : undefined}
              onSelect={(d) => d && setStartDate(format(d, "yyyy-MM-dd"))}
              initialFocus
            />
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn("w-[150px] justify-start text-left font-normal", !endDate && "text-muted-foreground")}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {endDate ? format(parseISO(endDate), "MMM dd, yyyy") : "End"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0 z-50" align="start">
            <Calendar
              mode="single"
              selected={endDate ? parseISO(endDate) : undefined}
              onSelect={(d) => d && setEndDate(format(d, "yyyy-MM-dd"))}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>

      {/* Table */}
      {loading ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">Loading…</CardContent></Card>
      ) : visible.length === 0 ? (
        <Card><CardContent className="py-10 text-center text-muted-foreground">No shifts match the current filters.</CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="py-2 px-4 font-medium">Date</th>
                  <th className="py-2 px-4 font-medium">Operator</th>
                  <th className="py-2 px-4 font-medium">Rig</th>
                  <th className="py-2 px-4 font-medium">Site</th>
                  <th className="py-2 px-4 font-medium">Shift</th>
                  <th className="py-2 px-4 font-medium text-right">Holes</th>
                  <th className="py-2 px-4 font-medium text-right">Metres</th>
                  <th className="py-2 px-4 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => {
                  const d = (r.shift_data ?? {}) as ShiftDataLike;
                  const sd = d.shiftDetails ?? {};
                  const holes = d.drillHoles ?? [];
                  const totalMetres = holes.reduce((s, h) => s + (h.depth ?? 0), 0);
                  return (
                    <tr
                      key={r.id}
                      className="border-b border-border/50 last:border-0 cursor-pointer hover:bg-accent/40 transition-colors"
                      onClick={() => setSelected(r)}
                    >
                      <td className="py-2 px-4">{safeFormatDate(sd.date, "d MMM yyyy")}</td>
                      <td className="py-2 px-4">{sd.operatorName ?? "—"}</td>
                      <td className="py-2 px-4">{sd.rigId ?? "—"}</td>
                      <td className="py-2 px-4">{sd.site ?? "—"}</td>
                      <td className="py-2 px-4 capitalize">{sd.shiftType ?? "—"}</td>
                      <td className="py-2 px-4 text-right">{holes.length}</td>
                      <td className="py-2 px-4 text-right">{totalMetres.toFixed(1)}</td>
                      <td className="py-2 px-4">
                        <Badge variant="secondary" className="capitalize">{r.status}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
